import { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CalendarDays, Newspaper } from "lucide-react";
import { BookCallButton } from "@/components/shared/BookCallButton";

const B = process.env.REACT_APP_BACKEND_URL;
const fmt = (s) => new Date(s).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const isPastEvent = (p) => !!p?.event_start && new Date(p.event_end || p.event_start) < new Date(new Date().toDateString());

// Approved dealer photos + upcoming events for a store page; events get schema.org Event markup for local search
export const StoreCommunity = ({ location }) => {
  const [data, setData] = useState({ photos: [], events: [], event_posts: [] });
  useEffect(() => { if (location?.id) axios.get(`${B}/api/public/locations/${location.id}/community`).then((r) => setData(r.data)).catch(() => {}); }, [location?.id]);
  const address = { "@type": "PostalAddress", streetAddress: location.address, addressLocality: location.city, addressRegion: location.state, postalCode: location.zip_code, addressCountry: "US" };
  const schema = data.events.map((e) => ({
    "@context": "https://schema.org", "@type": "Event", name: e.title, description: e.description || e.title,
    startDate: e.start, ...(e.end ? { endDate: e.end } : {}), eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: { "@type": "Place", name: location.name, address },
    organizer: { "@type": "Organization", name: "Prime Media Wireless", url: "https://primemediawireless.com" },
  }));
  return (
    <>
      {schema.length > 0 && <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>}
      {data.events.length > 0 && (
        <section className="py-12 bg-white" data-testid="store-events">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Upcoming events at {location.name}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.events.map((e) => (
                <div key={e.id} className="rounded-2xl border border-teal/30 bg-teal/5 overflow-hidden" data-testid={`store-event-${e.id}`}>
                  {e.flyer_url && <img src={`${B}${e.flyer_url}`} alt={`${e.title} flyer`} loading="lazy" className="w-full aspect-[4/5] object-cover" data-testid={`store-event-flyer-${e.id}`} />}
                  <div className="p-5">
                    <p className="flex items-center gap-2 text-teal text-sm font-semibold mb-1"><CalendarDays className="w-4 h-4" />{fmt(e.start)}</p>
                    <h3 className="font-heading font-semibold text-navy">{e.title}</h3>
                    {e.description && <p className="text-sm text-slate-600 mt-1">{e.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {data.event_posts?.length > 0 && (
        <section className="py-12 bg-slate-50" data-testid="store-event-posts">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">News &amp; events from {location.name}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.event_posts.map((p) => {
                const past = isPastEvent(p);
                return (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-teal" data-testid={`store-event-post-${p.slug}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-2 text-teal text-sm font-semibold"><Newspaper className="w-4 h-4" />{p.event_start ? fmt(p.event_start) : "Store news"}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${past ? "bg-slate-100 text-slate-500" : "bg-teal/10 text-teal"}`}>{past ? "Past event" : "Upcoming"}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-navy group-hover:text-teal transition-colors">{p.title}</h3>
                    {p.excerpt && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{p.excerpt}</p>}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
      {data.photos.length > 0 && (
        <section className="py-12 bg-slate-50" data-testid="store-photos">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Inside {location.name}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.photos.map((p) => (p.media_type === "video"
                ? <video key={p.id} src={p.media_url?.startsWith("/api/") ? `${B}${p.media_url}` : p.media_url} controls preload="metadata" playsInline className="w-full aspect-square object-cover rounded-xl bg-black" data-testid={`store-video-${p.id}`} />
                : <img key={p.id} src={p.image_url?.startsWith("/api/") ? `${B}${p.image_url}` : p.image_url} alt={p.description || `${location.name} store photo`} loading="lazy" className="w-full aspect-square object-cover rounded-xl" data-testid={`store-photo-${p.id}`} />))}
            </div>
          </div>
        </section>
      )}
      <section className="py-10 bg-navy" data-testid="store-callback">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="font-heading font-semibold text-lg text-white">Want a call back about this store or opening your own?</p>
          <BookCallButton city={location.city} state={location.state} label="Pick a time for a call" />
        </div>
      </section>
    </>
  );
};
